import { useCallback, useEffect, useRef, useState } from 'react';
import { validateAccount } from './coachProfileModel';
import { updateCoachAccount } from './coachProfileServices';

export const AUTOSAVE_MS = 1500;

// The save the last visit left running when the coach walked away; the next
// visit loads after it, so it never shows the name from before that edit.
let leaving = Promise.resolve(true);
export const afterLastVisit = () => leaving;

// First and last name that save as you go: a moment after the last
// keystroke, at once on Save, and when the coach leaves the hub. Saves run
// one after another, each sending the latest values, so an older copy never
// lands after a newer one. `onSaved(account)` gets the server's copy.
const useAccountForm = (account, onSaved) => {
  const [form, setForm] = useState({ firstName: account.firstName || '', lastName: account.lastName || '' });
  const [status, setStatus] = useState('saved');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const latest = useRef(form);
  const dirty = useRef(false);
  const timer = useRef(null);
  const queue = useRef(Promise.resolve(true));
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  const run = useCallback(async () => {
    const values = latest.current;
    const problems = validateAccount(values);
    if (Object.keys(problems).length) {
      setFieldErrors(problems);
      setStatus('error');
      setError('Check the highlighted fields.');
      return false;
    }
    dirty.current = false;
    setStatus('saving');
    try {
      const { account: saved } = await updateCoachAccount(values);
      setStatus(dirty.current ? 'dirty' : 'saved');
      setError('');
      setFieldErrors({});
      onSavedRef.current?.(saved);
      return true;
    } catch (e) {
      // Still unsaved, so leaving the page tries again.
      dirty.current = true;
      setStatus('error');
      setError(e.message || 'Could not save.');
      setFieldErrors(e.fields || {});
      return false;
    }
  }, []);

  // Resolves true once the server has the latest values.
  const save = useCallback(() => {
    clearTimeout(timer.current);
    queue.current = queue.current.then(run);
    return queue.current;
  }, [run]);

  const edit = useCallback(
    (patch) => {
      latest.current = { ...latest.current, ...patch };
      setForm(latest.current);
      setFieldErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => !(key in patch))));
      dirty.current = true;
      setStatus('dirty');
      clearTimeout(timer.current);
      timer.current = setTimeout(save, AUTOSAVE_MS);
    },
    [save],
  );

  const isDirty = useCallback(() => dirty.current, []);

  // Resolves true once the server has every edit: sends a pending one (or
  // retries a refused one), else waits for the save already on its way. The
  // phone's Done uses it before going back to the hub.
  const flush = useCallback(() => (dirty.current ? save() : queue.current), [save]);

  // Leaving the hub flushes an edit the autosave has not sent yet (or keeps
  // the save already on its way) for the next visit to wait on.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      leaving = dirty.current ? save() : queue.current;
    },
    [save],
  );

  return { form, edit, save, flush, isDirty, status, error, fieldErrors };
};

export default useAccountForm;
